import React, { useState, useEffect } from 'react';
import { X, User as UserIcon, ChevronLeft } from 'lucide-react';
import { UserGenderSettings } from './UserGenderSettings';
import { UserAgeSettings } from './UserAgeSettings';

interface ProfileSettingsProps {
  isVisible: boolean;
  onClose: () => void;
}

type SettingsView = 'menu' | 'gender' | 'age';

export function ProfileSettings({ isVisible, onClose }: ProfileSettingsProps) {
  const [currentView, setCurrentView] = useState<SettingsView>('menu');

  // Reset view to menu when modal is opened
  useEffect(() => {
    if (isVisible) {
      setCurrentView('menu');
    }
  }, [isVisible]);

  if (!isVisible) return null;

  // When a specific settings panel is shown, render it standalone
  if (currentView === 'gender') {
    return (
      <UserGenderSettings
        isVisible={true}
        onClose={() => {
          setCurrentView('menu');
          onClose();
        }}
      />
    );
  }

  if (currentView === 'age') {
    return (
      <UserAgeSettings
        isVisible={true}
        onClose={() => {
          setCurrentView('menu');
          onClose();
        }}
      />
    );
  }

  // Main menu view
  return (
    <div className="absolute top-16 right-4 w-80 bg-[#1C2431] border border-slate-700 rounded-lg shadow-xl z-30">
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <UserIcon className="w-5 h-5 text-purple-400" />
            <h3 className="text-white font-semibold">Profile Settings</h3>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Settings Options */}
        <div className="space-y-2">
          <button
            onClick={() => setCurrentView('gender')}
            className="w-full p-3 bg-[#0E1525] hover:bg-[#1C2431] border border-slate-600 hover:border-purple-400 rounded-lg text-left transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-white font-medium text-sm">Gender Settings</div>
                <div className="text-slate-400 text-xs mt-0.5">Update your gender preferences</div>
              </div>
              <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>

          <button
            onClick={() => setCurrentView('age')}
            className="w-full p-3 bg-[#0E1525] hover:bg-[#1C2431] border border-slate-600 hover:border-purple-400 rounded-lg text-left transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-white font-medium text-sm">Age Group Settings</div>
                <div className="text-slate-400 text-xs mt-0.5">Update your age group</div>
              </div>
              <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
